import {
  buildAddInstallment,
  buildAddPayee,
  buildDeletePayee,
  buildEditInstallment,
  buildEditPayee,
  buildEnsureLoanPaymentCategory,
  buildInstallmentChargeFixes,
  buildInstallmentCharges,
  buildPayAnyAmount,
  buildPayDue,
  buildRestorePayee,
  buildSetDueAmount,
  buildSetDueMinimum,
  buildSetDueStatus,
  buildSetPaymentsSettings,
  buildUndoPayment,
  findDue,
  formatPeso,
  localDate,
  newId,
  paymentPayFrom,
  paymentsMutation,
  pendingInstallmentCharges,
  singletonId,
  type BuildCtx,
  type DueRowStatus,
  type InstallmentDraft,
  type LocalDate,
  type Mutation,
  type PayeeDraft,
  type PayInput,
  type PaymentsSettings,
} from '@tahi/core';
import { makeMutation, monotonicNow } from '../mutation';
import { getBudgetSettingsView, listAccounts } from '../repos/budget';
import * as repo from '../repos/payments';
import { getUserSettings, listAllCategories } from '../repos/spending';
import { runCommand, type CommandEnv } from './dispatch';

export type PaymentsCommandResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

/** Seed marker in user_settings.seeds: Loan payment has been made (or restored) once. */
export const PAYMENTS_CATEGORY_SEED = 'payments.loan_payment.v1';

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

const today = (env: CommandEnv): LocalDate => localDate(new Date(), env.tz);
const money = (minor: number) => formatPeso(minor, { cents: 'auto' });

/**
 * v0.5: the account a payment leaves: picked on the pay sheet, else the payee's own, else (v0.7.4)
 * Payments' "Pay from by default", else Budget's "Pay from". A card statement payment too (a
 * transfer from that account to the card).
 */
async function payFrom(
  env: CommandEnv,
  picked: string | null | undefined,
  payee: { accountId?: string | null },
): Promise<string | null> {
  if (!env.usable('budget')) return null;
  const settings = await getBudgetSettingsView(env.db, env.userId);
  return paymentPayFrom(
    picked,
    payee.accountId,
    await repo.getPaymentsSettings(env.db, env.userId),
    settings,
    true,
    await listAccounts(env.db, env.userId),
  );
}

/** v0.7.4 Payments settings: "Pay from by default" and the remind days new payees start with. */
export async function setPaymentsSettings(
  env: CommandEnv,
  patch: Partial<PaymentsSettings>,
): Promise<PaymentsCommandResult> {
  const r = buildSetPaymentsSettings(env.userId, patch, ctx(env));
  if (!r.ok) return r;
  if (r.mutation) await runCommand(env, { mutations: [r.mutation] });
  return { ok: true };
}

let queue: Promise<unknown> = Promise.resolve();

/** Payments read a due before writing it; one at a time, so two quick taps never pay twice. */
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = queue.then(fn, fn);
  queue = run.catch(() => undefined);
  return run;
}

export function addPayee(
  env: CommandEnv,
  draft: PayeeDraft,
): Promise<PaymentsCommandResult<{ id: string }>> {
  return serial(async () => {
    const r = buildAddPayee(draft, today(env), ctx(env));
    if (!r.ok) return r;
    await runCommand(env, { mutations: [r.mutation] });
    return { ok: true, id: r.id };
  });
}

export function editPayee(
  env: CommandEnv,
  id: string,
  draft: PayeeDraft,
): Promise<PaymentsCommandResult> {
  return serial(async () => {
    const current = await repo.getPayee(env.db, env.userId, id);
    if (!current || current.deletedAt) return { ok: false, error: 'not_found' };
    const r = buildEditPayee(current, draft, ctx(env));
    if (!r.ok) return r;
    if (r.mutation) await runCommand(env, { mutations: [r.mutation] });
    return { ok: true };
  });
}

/** Soft delete (a card's installments too). Returns what undo needs. */
export function deletePayee(
  env: CommandEnv,
  id: string,
): Promise<PaymentsCommandResult<{ installmentIds: string[] }>> {
  return serial(async () => {
    const list = await repo.listInstallments(env.db, env.userId);
    const ms = buildDeletePayee({ id }, list, ctx(env));
    await runCommand(env, { mutations: ms });
    return {
      ok: true,
      installmentIds: list.filter((i) => i.cardId === id).map((i) => i.id),
    };
  });
}

export async function restorePayee(
  env: CommandEnv,
  id: string,
  installmentIds: readonly string[],
): Promise<void> {
  await runCommand(env, { mutations: buildRestorePayee(id, installmentIds, ctx(env)) });
}

async function loadDue(env: CommandEnv, payeeId: string, period: LocalDate) {
  const snap = await repo.loadPaymentsSnapshot(env.db, env.userId);
  const payee = snap.payees.find((p) => p.id === payeeId) ?? null;
  const due = payee ? findDue(snap, payeeId, period, today(env)) : null;
  return { snap, payee, due };
}

/**
 * Pay a due in full or in part. The payment row plus the payments.paid-expense rule's expense
 * (a transfer for a card statement) and Budget through it, in one transaction:
 * "Paid · Payments ┄ Spending ₱4,500 ┄ Budget".
 */
export function payDue(
  env: CommandEnv,
  payeeId: string,
  period: LocalDate,
  input: PayInput & { accountId?: string | null },
): Promise<PaymentsCommandResult<{ paymentId: string; remainingMinor: number }>> {
  return serial(async () => {
    const { payee, due } = await loadDue(env, payeeId, period);
    if (!payee || !due) return { ok: false, error: 'not_found' };
    const r = buildPayDue(
      env.userId,
      due,
      payee,
      input,
      { spending: env.usable('spending'), accountId: await payFrom(env, input.accountId, payee) },
      ctx(env),
    );
    if (!r.ok) return r;
    await runCommand(env, {
      mutations: r.mutations,
      event: r.event,
      toast: {
        headline: 'Paid',
        details: {
          spending: due.transfer
            ? `${money(input.amountMinor)} transfer`
            : money(input.amountMinor),
        },
      },
    });
    const paid = due.paidMinor + input.amountMinor;
    const owed = input.setAmount ? paid : due.amountMinor;
    return { ok: true, paymentId: r.payment.id, remainingMinor: Math.max(0, owed - paid) };
  });
}

/**
 * "Pay any amount" on a loan: toward the nearest open planned due when there is one (so it clears
 * and Budget counts the money once), else a payment of its own.
 */
export function payExtra(
  env: CommandEnv,
  payeeId: string,
  input: { amountMinor: number; paidDate: LocalDate; accountId?: string | null },
): Promise<PaymentsCommandResult<{ paymentId: string }>> {
  return serial(async () => {
    const snap = await repo.loadPaymentsSnapshot(env.db, env.userId);
    const payee = snap.payees.find((p) => p.id === payeeId) ?? null;
    if (!payee) return { ok: false, error: 'not_found' };
    const r = buildPayAnyAmount(
      env.userId,
      snap,
      payee,
      input,
      { spending: env.usable('spending'), accountId: await payFrom(env, input.accountId, payee) },
      ctx(env),
      today(env),
    );
    if (!r.ok) return r;
    await runCommand(env, {
      mutations: r.mutations,
      event: r.event,
      toast: { headline: 'Paid', details: { spending: money(input.amountMinor) } },
    });
    return { ok: true, paymentId: r.payment.id };
  });
}

/** Undo a payment (the toast's Undo, or from the due's payment list). */
export function undoPayment(env: CommandEnv, paymentId: string): Promise<PaymentsCommandResult> {
  return serial(async () => {
    const snap = await repo.loadPaymentsSnapshot(env.db, env.userId);
    const p = snap.payments.find((x) => x.id === paymentId);
    if (!p) return { ok: false, error: 'not_found' };
    await runCommand(env, { mutations: buildUndoPayment(p, ctx(env)) });
    return { ok: true };
  });
}

/** Skip / Close ("rest won't be paid") / back to open. */
export function setDueStatus(
  env: CommandEnv,
  payeeId: string,
  period: LocalDate,
  status: DueRowStatus,
): Promise<PaymentsCommandResult> {
  return serial(async () => {
    const { due } = await loadDue(env, payeeId, period);
    if (!due) return { ok: false, error: 'not_found' };
    const ms = buildSetDueStatus(due, status, ctx(env));
    if (ms.length > 0) await runCommand(env, { mutations: ms });
    return { ok: true };
  });
}

/** The real amount (utility bill, card statement) or null to go back to the estimate. */
export function setDueAmount(
  env: CommandEnv,
  payeeId: string,
  period: LocalDate,
  amountMinor: number | null,
): Promise<PaymentsCommandResult> {
  return serial(async () => {
    const { due } = await loadDue(env, payeeId, period);
    if (!due) return { ok: false, error: 'not_found' };
    const r = buildSetDueAmount(due, amountMinor, ctx(env));
    if (!r.ok) return r;
    await runCommand(env, { mutations: r.mutations });
    return { ok: true };
  });
}

export function setDueMinimum(
  env: CommandEnv,
  payeeId: string,
  period: LocalDate,
  minimumMinor: number | null,
): Promise<PaymentsCommandResult> {
  return serial(async () => {
    const { due } = await loadDue(env, payeeId, period);
    if (!due) return { ok: false, error: 'not_found' };
    const r = buildSetDueMinimum(due, minimumMinor, ctx(env));
    if (!r.ok) return r;
    await runCommand(env, { mutations: r.mutations });
    return { ok: true };
  });
}

export async function addInstallment(
  env: CommandEnv,
  cardId: string,
  draft: InstallmentDraft,
): Promise<PaymentsCommandResult<{ id: string }>> {
  const r = buildAddInstallment(cardId, draft, ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation] });
  await runInstallmentCatchUp(env);
  return { ok: true, id: r.id };
}

/**
 * Edit an installment. Its charges already written that aren't on a paid statement follow the new
 * terms (or are soft-deleted when the month is gone), in the same transaction; months newly due
 * are written by the catch-up.
 */
export async function editInstallment(
  env: CommandEnv,
  id: string,
  draft: InstallmentDraft,
): Promise<PaymentsCommandResult> {
  const snap = await repo.loadPaymentsSnapshot(env.db, env.userId);
  const current = snap.installments.find((i) => i.id === id);
  if (!current) return { ok: false, error: 'not_found' };
  const c = ctx(env);
  const r = buildEditInstallment(current, draft, c);
  if (!r.ok) return r;
  if (r.mutation) {
    const edited = { ...current, ...draft, item: draft.item.trim().replace(/\s+/g, ' ') };
    const fixes = buildInstallmentChargeFixes(snap, edited, today(env), c);
    await runCommand(env, { mutations: [r.mutation, ...fixes] });
  }
  await runInstallmentCatchUp(env);
  return { ok: true };
}

export async function deleteInstallment(env: CommandEnv, id: string): Promise<void> {
  await runCommand(env, {
    mutations: [makeMutation('installments', 'delete', id, undefined, 'payments')],
  });
}

export async function restoreInstallment(env: CommandEnv, id: string): Promise<void> {
  await runCommand(env, {
    mutations: [makeMutation('installments', 'insert', id, {}, 'payments')],
  });
}

let catchingUp: Promise<void> | null = null;

/**
 * Writes every installment month whose charge date has come (one card expense each, decision 06),
 * on deterministic ids. Runs while Payments and Spending are both usable: app start, either module
 * turned on, the Payments screen opening, an installment added. Months that came while Spending
 * was off are written now (OPEN-QUESTIONS v0.5 Q5).
 */
export function runInstallmentCatchUp(env: CommandEnv): Promise<void> {
  if (!env.usable('payments') || !env.usable('spending')) return Promise.resolve();
  catchingUp ??= (async () => {
    try {
      const snap = await repo.loadPaymentsSnapshot(env.db, env.userId);
      const pending = pendingInstallmentCharges(
        snap,
        await repo.listInstallmentEntryIds(env.db, env.userId),
        today(env),
      );
      if (pending.length === 0) return;
      await runCommand(env, { mutations: buildInstallmentCharges(pending, ctx(env)) });
    } finally {
      catchingUp = null;
    }
  })();
  return catchingUp;
}

function seedMarker(
  env: CommandEnv,
  seeds: Record<string, string> | null,
  key: string,
  now: string,
): Mutation {
  const settingsId = singletonId(env.userId, 'user_settings');
  const next = { ...seeds, [key]: now };
  return seeds
    ? paymentsMutation({ now, tz: env.tz, newId }, 'user_settings', 'update', settingsId, {
        seeds: next,
      })
    : paymentsMutation(
        { now, tz: env.tz, newId },
        'user_settings',
        'insert',
        settingsId,
        { weekStart: 1, seeds: next },
        { ifAbsent: true },
      );
}

/**
 * Loan payment (v0.1.1 note): created when Payments is on, on its seeded id; restored with a plain
 * insert when the v0.1.1 cleanup (or the user, earlier) deleted it, keeping any rename. Once per
 * user, so a Loan payment the user deletes later stays deleted.
 */
export async function ensureLoanPaymentCategory(env: CommandEnv): Promise<void> {
  const settings = await getUserSettings(env.db, env.userId);
  if (settings?.seeds[PAYMENTS_CATEGORY_SEED]) return;
  const c = ctx(env);
  const ms = buildEnsureLoanPaymentCategory(
    env.userId,
    await listAllCategories(env.db, env.userId),
    c,
  );
  await runCommand(env, {
    mutations: [...ms, seedMarker(env, settings?.seeds ?? null, PAYMENTS_CATEGORY_SEED, c.now)],
  });
}

/** Waits for this session's first sync, so a Loan payment row deleted on the server is here first. */
function afterFirstSync(env: CommandEnv, run: () => Promise<void>): Promise<void> {
  const status = env.sync.status;
  const go = () =>
    run().catch((err: unknown) => {
      console.warn('Tahi: a Payments setup step failed', err);
    });
  if (!status || status.get().lastSyncedAt !== null) return go();
  return new Promise<void>((resolve) => {
    const unsubscribe = status.subscribe(() => {
      if (status.get().lastSyncedAt === null) return;
      unsubscribe();
      void go().then(resolve);
    });
  });
}

/** The Payments usable hook: the Loan payment category and the installment catch-up after sync. */
export async function onPaymentsUsable(env: CommandEnv): Promise<void> {
  void afterFirstSync(env, async () => {
    await ensureLoanPaymentCategory(env);
    await runInstallmentCatchUp(env);
  });
}
